(ns practitest.frontend.project-step-params-settings.events
  (:require
    [ajax.core :as ajax]
    [clojure.string :as str]
    [re-frame.core :as re-frame]
    [practitest.frontend.common.i18n :refer [get-translation]]
    [practitest.frontend.common.mixpanel :as mixpanel]
    [practitest.frontend.components.notifications :as notifications]
    [practitest.frontend.routing.backend-routes :as broutes]
    [practitest.frontend.subs :as subs]))

(def ^:private db-key :project-step-params-settings)

(def list-types #{"list" "url_list"})

;; form-id/field-id for the shared list-builder component (possible values editor)
(def possible-values-form-id :step-params-form)
(def possible-values-field-id :possible-values)

(defn- possible-values [db]
  (get-in db [:form-states2 possible-values-form-id :fields possible-values-field-id :value] []))

(defn- set-possible-values [db values]
  (assoc-in db [:form-states2 possible-values-form-id :fields possible-values-field-id]
            {:value values :dirty? true}))

(defn- clear-modal [db]
  (-> db
      (update db-key dissoc :modal)
      (update :form-states2 dissoc possible-values-form-id)))

;; ---- data fetch ----

(re-frame/reg-event-fx
  ::load!
  (fn [_ _]
    {:dispatch [::fetch-params]
     ::mixpanel/track ["Step Parameters" {"Action" "Page View"}]}))

(re-frame/reg-event-fx
  ::fetch-params
  (fn [{:keys [db]} _]
    {:db (assoc-in db [db-key :loading?] true)
     :http-xhrio {:method          :get
                  :uri             (broutes/get-url "projects#step_params"
                                                    {:project_id (subs/get-current-project-id db)})
                  :format          (ajax/json-request-format)
                  :response-format (ajax/json-response-format {:keywords? true})
                  :on-success      [::params-fetched]
                  :on-failure      [::request-failed]}}))

(re-frame/reg-event-db
  ::params-fetched
  (fn [db [_ params-by-name]]
    (let [rows (mapv (fn [[key-name param]]
                        (assoc param :key_name (name key-name)))
                      params-by-name)]
      (-> db
          (assoc-in [db-key :rows] rows)
          (assoc-in [db-key :loading?] false)))))

(re-frame/reg-event-fx
  ::request-failed
  (fn [{:keys [db]} _]
    {:db       (assoc-in db [db-key :loading?] false)
     :dispatch [::notifications/error2
                (get-translation "generic.error.general" "Something went wrong. Please try again.")]}))

(re-frame/reg-event-db
  ::reset
  (fn [db _]
    (dissoc db db-key)))

;; ---- create / edit modal ----

(defn- modal-state [mode row]
  {:mode     mode
   :key_name (:key_name row)
   :name     (:name row "")
   :type     (:type row "text")
   :val      (:val row "")
   :saving?  false})

(re-frame/reg-event-db
  ::open-create-modal
  (fn [db _]
    (-> db
        (assoc-in [db-key :modal] (modal-state :create nil))
        (set-possible-values []))))

(re-frame/reg-event-db
  ::open-edit-modal
  (fn [db [_ row]]
    (-> db
        (assoc-in [db-key :modal] (modal-state :edit row))
        (set-possible-values (vec (:step_field_list_values row))))))

(re-frame/reg-event-db
  ::close-modal
  (fn [db _]
    (clear-modal db)))

(re-frame/reg-event-db
  ::set-modal-field
  (fn [db [_ field value]]
    (cond-> (assoc-in db [db-key :modal field] value)
      ;; type change invalidates the previous default value
      (= field :type)
      (assoc-in [db-key :modal :val] ""))))

(re-frame/reg-event-db
  ::delete-possible-value
  (fn [db [_ value]]
    (cond-> (set-possible-values db (vec (remove #(= % value) (possible-values db))))
      (= value (get-in db [db-key :modal :val]))
      (assoc-in [db-key :modal :val] ""))))

(re-frame/reg-event-fx
  ::save-modal
  (fn [{:keys [db]} _]
    (let [{:keys [mode key_name name type val]} (get-in db [db-key :modal])
          project-id (subs/get-current-project-id db)
          list-type? (contains? list-types type)
          possible_values (possible-values db)
          params (cond-> {:name                   name
                          :val                    (if list-type? "" val)
                          :step                   {:type type :value (if list-type? val "")}
                          :step_field_list_values (str/join "," possible_values)}
                   (= mode :edit)
                   (assoc :key_name key_name))
          route-name (if (= mode :edit) "projects#update_step_param" "projects#create_step_param")]
      {:db         (assoc-in db [db-key :modal :saving?] true)
       :http-xhrio {:method          :get
                    :uri             (broutes/get-url route-name {:project_id project-id})
                    :params          params
                    :format          (ajax/json-request-format)
                    :response-format (ajax/json-response-format {:keywords? true})
                    :on-success      [::save-modal-response mode type]
                    :on-failure      [::save-modal-failed]}})))

(re-frame/reg-event-fx
  ::save-modal-failed
  (fn [{:keys [db]} _]
    {:db       (assoc-in db [db-key :modal :saving?] false)
     :dispatch [::notifications/error2
                (get-translation "generic.error.general" "Something went wrong. Please try again.")]}))

(re-frame/reg-event-fx
  ::save-modal-response
  (fn [{:keys [db]} [_ mode type {:keys [success error_msg]}]]
    (if success
      {:db         (clear-modal db)
       :dispatch-n [[::fetch-params]
                    [::notifications/success2
                     (if (= mode :edit)
                       (get-translation "step-parameters.notify.updated" "Step Parameter was successfully updated")
                       (get-translation "test.step.param.successfully.created" "Step parameter was successfully created"))]]
       ::mixpanel/track ["Step Parameters" {"Action" (if (= mode :edit) "Edit" "New")
                                            "Type"   type}]}
      {:db       (assoc-in db [db-key :modal :saving?] false)
       :dispatch [::notifications/error2 error_msg]})))

;; ---- delete ----

(re-frame/reg-event-fx
  ::delete-param
  (fn [{:keys [db]} [_ key_name]]
    {:http-xhrio {:method          :get
                  :uri             (broutes/get-url "projects#delete_step_param"
                                                     {:project_id (subs/get-current-project-id db)})
                  :params          {:name key_name}
                  :format          (ajax/json-request-format)
                  :response-format (ajax/json-response-format {:keywords? true})
                  :on-success      [::delete-param-response]
                  :on-failure      [::request-failed]}}))

(re-frame/reg-event-fx
  ::delete-param-response
  (fn [_ [_ {:keys [success error_msg]}]]
    (if success
      {:dispatch-n [[::fetch-params]
                    [::notifications/success2
                     (get-translation "step-parameters.notify.deleted" "Step Parameter was successfully deleted")]]
       ::mixpanel/track ["Step Parameters" {"Action" "Delete"}]}
      {:dispatch [::notifications/error2 error_msg]})))

;; ---- subs ----

(re-frame/reg-sub ::state (fn [db _] (get db db-key)))
(re-frame/reg-sub ::rows :<- [::state] (fn [s _] (or (:rows s) [])))
(re-frame/reg-sub ::loading? :<- [::state] (fn [s _] (boolean (:loading? s))))
(re-frame/reg-sub ::modal :<- [::state] (fn [s _] (:modal s)))
